// src/pages/DatabaseScreen.js
import { useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Database, Network, Plus, Server, Table } from "lucide-react";

import { DatabaseProvider } from "./databaseContext";

// Child screens
import SummaryScreen from "./screens/SummaryScreen";
import DatabaseListScreen from "./screens/DatabaseListScreen";
import VMScreen from "./screens/VMScreen";
import ConnectionsScreen from "./screens/ConnectionsScreen";
import TablesScreen from "./screens/TablesScreen";

const DatabaseScreen = () => {
  const [activeTab, setActiveTab] = useState("summary");
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [notification, setNotification] = useState(null);
  const [hostFilter, setHostFilter] = useState(null);
  const [selectedHost, setSelectedHost] = useState(null);

  const handleTabChange = (tab, params = {}) => {
    setActiveTab(tab);
    if (params.hostFilter) {
      setHostFilter(params.hostFilter);
      // Update URL with host filter
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("host", params.hostFilter);
      window.history.pushState({}, "", `${window.location.pathname}?${searchParams.toString()}`);
    }
    if (params.host) {
      setSelectedHost(params.host);
    }
  };

  const handleDatabaseSelect = (dbName) => {
    setSelectedDatabase(dbName);
  };

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  return (
    <DatabaseProvider>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Database Dashboard</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Monitor and manage your databases, connections, and tables
                </p>
              </div>
              <Link
                to="/pam/databases/add/linux"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
              >
                <Plus className="h-4 w-4 mr-2" />
                Install Database Agent
              </Link>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="flex overflow-x-auto scrollbar-hide justify-between px-6">
              <button
                onClick={() => handleTabChange("summary")}
                className={`flex items-center px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
                  activeTab === "summary"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Summary
              </button>
              <button
                onClick={() => handleTabChange("databases")}
                className={`flex items-center px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
                  activeTab === "databases"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Database className="h-4 w-4 mr-2" />
                Databases
              </button>
              <button
                onClick={() => handleTabChange("vms")}
                className={`flex items-center px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
                  activeTab === "vms"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Server className="h-4 w-4 mr-2" />
                Hosts
              </button>
              <button
                onClick={() => handleTabChange("connections")}
                className={`flex items-center px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
                  activeTab === "connections"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Network className="h-4 w-4 mr-2" />
                Connections
              </button>
              <button
                onClick={() => handleTabChange("tables")}
                className={`flex items-center px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
                  activeTab === "tables"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Table className="h-4 w-4 mr-2" />
                Tables
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === "summary" && (
                <SummaryScreen showNotification={showNotification} onTabChange={handleTabChange} />
              )}
              {activeTab === "databases" && (
                <DatabaseListScreen
                  showNotification={showNotification}
                  onTabChange={handleTabChange}
                  onDatabaseSelect={handleDatabaseSelect}
                />
              )}
              {activeTab === "vms" && (
                <VMScreen showNotification={showNotification} onTabChange={handleTabChange} />
              )}
              {activeTab === "connections" && (
                <ConnectionsScreen showNotification={showNotification} />
              )}
              {activeTab === "tables" && (
                <TablesScreen
                  showNotification={showNotification}
                  selectedDatabase={selectedDatabase}
                  selectedHost={selectedHost}
                />
              )}
            </div>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div
            className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg text-white flex items-center ${
              notification.type === "error" ? "bg-red-500" : "bg-green-500"
            } transition-opacity duration-300 ease-in-out`}
          >
            <span className="text-sm font-medium">{notification.message}</span>
          </div>
        )}
      </div>
    </DatabaseProvider>
  );
};

export default DatabaseScreen;
